/**
 * Reference Copy page — a print-faithful reproduction of the FCC ULS
 * "License — Reference Copy" (form 601-ULSHS1).
 *
 * Rendered as a standalone document (outside the app Layout) so browser print /
 * "Save as PDF" produces a clean page without app chrome. Pulls the composed
 * payload from GET /api/v1/licenses/{callSign}/reference.
 */
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getReferenceCopy } from '../services/licenses'
import type { ReferenceCopy, FCCEntity, FCCLocation, FCCFrequency } from '../types'
import PageHeading from '../components/PageHeading'
import { REFERENCE_COPY_HELP } from '../content/pageHelp'

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    const d = new Date(dateStr)
    return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`
  } catch {
    return dateStr
  }
}

function formatDateTime(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString()
  } catch {
    return dateStr
  }
}

function formatPhone(phone: string | null): string {
  if (!phone) return '—'
  const d = phone.replace(/\D/g, '')
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
  return phone
}

function formatFreq(mhz: number | null): string {
  if (mhz == null) return '—'
  return mhz.toFixed(5)
}

function entityName(e: FCCEntity): string {
  if (e.entity_name) return e.entity_name
  const parts = [e.first_name, e.middle_initial ? `${e.middle_initial}.` : null, e.last_name, e.suffix].filter(Boolean)
  return parts.length ? parts.join(' ') : '—'
}

function dms(
  deg: number | null, min: number | null, sec: number | null, dir: string | null,
): string {
  if (deg == null) return '—'
  const parts = [`${deg}°`]
  if (min != null) parts.push(`${min}'`)
  if (sec != null) parts.push(`${sec.toFixed(1)}"`)
  if (dir) parts.push(dir)
  return parts.join(' ')
}

function locationLine(loc: FCCLocation): string {
  return [loc.location_city, loc.location_county && `${loc.location_county} Co.`, loc.location_state]
    .filter(Boolean).join(', ')
}

const AREA_OF_OP_LABELS: Record<string, string> = {
  C: 'Countywide', S: 'Statewide', N: 'Nationwide', T: 'Territory-wide', R: 'Regionwide',
}

/** Mobile / area-of-operation description, e.g. "Countywide: KEITH, NE". */
function areaOfOperation(loc: FCCLocation): string {
  if (!loc.area_of_operation_code) return ''
  const label = AREA_OF_OP_LABELS[loc.area_of_operation_code] ?? `Area code ${loc.area_of_operation_code}`
  const where = [loc.location_county && `${loc.location_county}`, loc.location_state].filter(Boolean).join(', ')
  return where ? `${label}: ${where}` : label
}

/** A labelled field for the header grid. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm text-gray-900">{children ?? '—'}</div>
    </div>
  )
}

/**
 * One party's name, address and contact details — used for the Licensee and for
 * every other party FCC lists on the license.
 */
function PartyDetails({ party }: { party: FCCEntity }) {
  return (
    <div className="text-sm">
      <div className="font-semibold">{entityName(party)}</div>
      {party.attention_line && <div>ATTN: {party.attention_line}</div>}
      {party.street_address && <div>{party.street_address}</div>}
      {party.po_box && <div>PO Box {party.po_box}</div>}
      <div>{[party.city, party.state, party.zip_code].filter(Boolean).join(' ')}</div>
      <div className="mt-1 text-gray-600">
        {party.phone && <span className="mr-4">P: {formatPhone(party.phone)}</span>}
        {party.fax && <span className="mr-4">F: {formatPhone(party.fax)}</span>}
        {party.email && <span>{party.email}</span>}
      </div>
    </div>
  )
}

/** Falls back to the raw code rather than dropping a party we have no label for. */
function partyRole(party: FCCEntity): string {
  const code = (party.entity_type || '').trim()
  return party.entity_type_label || (code ? `Party (${code})` : 'Other party')
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6 break-inside-avoid">
      <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 border-b-2 border-gray-800 pb-1 mb-3">
        {title}
      </h2>
      {children}
    </section>
  )
}

export default function ReferenceCopyPage() {
  const { callSign } = useParams<{ callSign: string }>()
  const [ref, setRef] = useState<ReferenceCopy | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!callSign) return
    let cancelled = false
    setLoading(true); setError(''); setNotFound(false)
    getReferenceCopy(callSign)
      .then((data) => { if (!cancelled) setRef(data) })
      .catch((e: Error) => {
        if (cancelled) return
        if (e.message.includes('404') || e.message.toLowerCase().includes('not found')) setNotFound(true)
        else setError(e.message || 'Failed to load reference copy')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [callSign])

  if (loading) return <div className="p-8 text-center text-gray-500">Loading reference copy…</div>
  if (notFound) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg font-semibold text-gray-900 mb-2">License {callSign} not found</p>
        <Link to="/search" className="text-primary-600 hover:underline">Back to Search</Link>
      </div>
    )
  }
  if (error) return <div className="p-8"><div className="alert alert-error">{error}</div></div>
  if (!ref) return null

  // Company logo: prefer an uploaded logo (served publicly), else an external URL.
  const logoSrc = ref.has_company_logo ? '/api/v1/branding/logo' : (ref.company_logo_url || null)

  // Group frequencies and emissions by (location, antenna) for the matrix.
  const freqsByLocAnt = new Map<string, FCCFrequency[]>()
  for (const f of ref.frequencies) {
    const key = `${f.location_number ?? 0}|${f.antenna_number ?? 1}`
    if (!freqsByLocAnt.has(key)) freqsByLocAnt.set(key, [])
    freqsByLocAnt.get(key)!.push(f)
  }
  const emissionsFor = (loc: number, ant: number, freq: number | null): string[] => {
    const ems = ref.emissions
      .filter((e) =>
        (e.location_number ?? 0) === loc &&
        (e.antenna_number ?? 1) === ant &&
        e.frequency_assigned != null && freq != null &&
        Math.abs(e.frequency_assigned - freq) < 1e-4)
      .map((e) => e.emission_designator)
      .filter((d): d is string => Boolean(d))
    return ems
  }
  const antennasFor = (loc: number) =>
    ref.antennas.filter((a) => (a.location_number ?? 0) === loc)
  // Construction deadline + actual date for a given (loc, ant, frequency)
  const buildoutFor = (loc: number, ant: number, freq: number | null) =>
    ref.buildouts.find((b) =>
      (b.location_number ?? 0) === loc &&
      (b.antenna_number ?? 1) === ant &&
      b.frequency_assigned != null && freq != null &&
      Math.abs(b.frequency_assigned - freq) < 1e-4) ?? null

  // Every party except the one already shown as the Licensee. Compared by id
  // rather than by entity_type, so a license whose only party is a contact still
  // renders it once (as the licensee) instead of twice.
  const otherParties = ref.entities.filter((e) => e.id !== ref.licensee?.id)

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white py-6 print:py-0">
      {/* Document-like print output: real page margins, US Letter, exact colors */}
      <style>{`@media print { @page { size: letter; margin: 0.6in; } html, body { background: #fff !important; } }`}</style>

      {/* Action bar — hidden when printing */}
      <div className="max-w-4xl mx-auto px-4 mb-2 flex items-center print:hidden">
        <Link to={`/licenses/${ref.call_sign}`} className="tap text-primary-600 hover:text-primary-800 text-sm font-medium gap-1.5">
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
          Back to {ref.call_sign}
        </Link>
      </div>

      {/* On-screen title; the explainer behind its "i" opens on the first visit. */}
      <PageHeading
        title={`Reference copy — ${ref.call_sign}`}
        help={REFERENCE_COPY_HELP}
        className="max-w-4xl mx-auto px-4 mb-4 print:hidden"
      >
        <button onClick={() => window.print()} className="btn btn-primary text-sm">
          Print / Save as PDF
        </button>
      </PageHeading>

      {/* The document */}
      <div className="max-w-4xl mx-auto bg-white shadow print:shadow-none p-8 print:p-0 text-gray-900">
        {/* Title + disclaimer — company branding on the left, FCC identity centered */}
        <div className="border-b-2 border-gray-800 pb-3 mb-4">
          <div className="flex items-start justify-between gap-4">
            {/* Company branding (our shop) */}
            <div className="flex items-center gap-3 min-w-0">
              {logoSrc && (
                <img
                  src={logoSrc}
                  alt={ref.company_name ?? 'Company logo'}
                  className="h-12 w-auto max-w-[180px] object-contain"
                  style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' } as React.CSSProperties}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                />
              )}
              {ref.company_name && (
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{ref.company_name}</div>
                  {ref.company_website && (
                    <div className="text-xs text-gray-500 truncate">{ref.company_website}</div>
                  )}
                </div>
              )}
            </div>
            {/* FCC identity */}
            <div className="text-right shrink-0">
              <div className="text-xs text-gray-500">Federal Communications Commission</div>
              <h1 className="text-lg font-bold tracking-wide">Radio Station Authorization</h1>
              <div className="text-xs font-semibold text-gray-600">Reference Copy</div>
            </div>
          </div>
          <div className="text-xs text-red-700 font-medium mt-2 text-center">
            REFERENCE COPY — generated by {ref.company_name || 'FCC License Manager'}. This is not an official FCC license.
          </div>
        </div>

        {/* Authorization header */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
          <Field label="Call Sign">
            <span className="font-mono font-bold text-base">{ref.call_sign}</span>
          </Field>
          <Field label="File Number">{ref.uls_file_number}</Field>
          <Field label="FRN">{ref.frn ? <span className="font-mono">{ref.frn}</span> : '—'}</Field>
          <Field label="Status">{ref.status_description}</Field>

          <Field label="Radio Service">
            {ref.radio_service_label
              ? `${ref.radio_service_code} — ${ref.radio_service_label}`
              : ref.radio_service_code}
          </Field>
          <Field label="Regulatory Status">{ref.regulatory_status}</Field>
          <Field label="Issuing Bureau">{ref.issuing_bureau}</Field>

          <Field label="Grant Date">{formatDate(ref.grant_date)}</Field>
          <Field label="Effective Date">{formatDate(ref.effective_date)}</Field>
          <Field label="Expiration Date">{formatDate(ref.expired_date)}</Field>
          <Field label="Print Date">{formatDateTime(ref.print_date)}</Field>
        </div>

        {/* Licensee */}
        <Section title="Licensee">
          {ref.licensee
            ? <PartyDetails party={ref.licensee} />
            : <p className="text-sm text-gray-500">No licensee on file.</p>}
        </Section>

        {/* Contact / owner. FCC lists a filing agent on most licenses and it is
            usually who you actually call about a renewal — the licensee is often
            a county or district that never answers. */}
        {otherParties.length > 0 && (
          <Section title="Contacts & Other Parties">
            <div className="grid gap-4 sm:grid-cols-2">
              {otherParties.map((party) => (
                <div key={party.id}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-0.5">
                    {partyRole(party)}
                  </div>
                  <PartyDetails party={party} />
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Sites + antenna/frequency/emission matrix */}
        {ref.locations.length > 0 && (
          <Section title="Sites & Equipment">
            <div className="space-y-5">
              {ref.locations.map((loc) => {
                const ants = antennasFor(loc.location_number)
                return (
                  <div key={loc.id} className="break-inside-avoid">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
                      <span className="font-semibold">Location {loc.location_number}</span>
                      {loc.location_type_code === 'M' && (
                        <span className="text-xs font-medium text-blue-700 bg-blue-50 px-1.5 rounded">Mobile — Area of Operation</span>
                      )}
                      {loc.location_address && <span>{loc.location_address}</span>}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-xs text-gray-700 mt-1">
                      {locationLine(loc) && <div className="col-span-2">{locationLine(loc)}</div>}
                      {areaOfOperation(loc) && <div className="col-span-2 font-medium text-gray-800">{areaOfOperation(loc)}</div>}
                      {(loc.lat_degrees != null || loc.long_degrees != null) && (
                        <div className="col-span-2 font-mono">
                          {dms(loc.lat_degrees, loc.lat_minutes, loc.lat_seconds, loc.lat_direction)} /{' '}
                          {dms(loc.long_degrees, loc.long_minutes, loc.long_seconds, loc.long_direction)}
                        </div>
                      )}
                      {loc.ground_elevation != null && <div>Ground Elev: {loc.ground_elevation} m</div>}
                      {loc.height_of_support_structure != null && <div>Struct Ht: {loc.height_of_support_structure} m</div>}
                      {loc.tower_registration_number && <div>ASR #: {loc.tower_registration_number}</div>}
                    </div>

                    {ants.length > 0 && (
                      <div className="overflow-x-auto print:overflow-visible">
                        <table className="min-w-full text-xs mt-2 border-t border-gray-200">
                          <thead>
                            <tr className="text-left text-gray-500">
                              <th className="py-1 pr-3 font-medium">Ant</th>
                              <th className="py-1 pr-3 font-medium">Ht/Tip (m)</th>
                              <th className="py-1 pr-3 font-medium">AAT (m)</th>
                              <th className="py-1 pr-3 font-medium">Azimuth</th>
                              <th className="py-1 pr-3 font-medium">Frequencies (MHz) — Emission(s) — Power / ERP</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ants.map((a) => {
                              const af = freqsByLocAnt.get(`${loc.location_number}|${a.antenna_number ?? 1}`) ?? []
                              return (
                                <tr key={a.id} className="border-t border-gray-100 align-top">
                                  <td className="py-1 pr-3 font-mono">{a.antenna_number}</td>
                                  <td className="py-1 pr-3">{a.height_to_tip_meters ?? '—'}</td>
                                  <td className="py-1 pr-3">{a.aat_meters ?? '—'}</td>
                                  <td className="py-1 pr-3">{a.azimuth != null ? `${a.azimuth}°` : '—'}</td>
                                  <td className="py-1 pr-3">
                                    {af.length === 0 ? '—' : (
                                      <ul className="space-y-0.5">
                                        {af.map((f) => {
                                          const ems = emissionsFor(loc.location_number, a.antenna_number ?? 1, f.frequency)
                                          const emText = ems.length ? ems.join(', ') : (f.emission_code || '—')
                                          const bo = buildoutFor(loc.location_number, a.antenna_number ?? 1, f.frequency)
                                          return (
                                            <li key={f.id} className="font-mono">
                                              {formatFreq(f.frequency)} — {emText}
                                              {(f.power_output != null || f.power_erp != null) && (
                                                <span className="text-gray-500">
                                                  {' '}— {f.power_output != null ? `${f.power_output}W` : '—'}
                                                  {' '}/ {f.power_erp != null ? `${f.power_erp}W ERP` : '—'}
                                                </span>
                                              )}
                                              {bo && (bo.buildout_deadline || bo.buildout_date) && (
                                                <span className="text-gray-400">
                                                  {bo.buildout_deadline && <>{' '}— constr. due {formatDate(bo.buildout_deadline)}</>}
                                                  {bo.buildout_date && <>{' '}· built {formatDate(bo.buildout_date)}</>}
                                                </span>
                                              )}
                                            </li>
                                          )
                                        })}
                                      </ul>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Section>
        )}

        {/* Control points */}
        {ref.control_points.length > 0 && (
          <Section title="Control Points">
            <div className="overflow-x-auto print:overflow-visible">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-1 pr-4 font-medium">#</th>
                    <th className="py-1 pr-4 font-medium">Address</th>
                    <th className="py-1 pr-4 font-medium">City / County / State</th>
                    <th className="py-1 pr-4 font-medium">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {ref.control_points.map((cp) => (
                    <tr key={cp.id} className="border-t border-gray-100">
                      <td className="py-1 pr-4">{cp.control_point_number}</td>
                      <td className="py-1 pr-4">{cp.address || '—'}</td>
                      <td className="py-1 pr-4">{[cp.city, cp.county && `${cp.county} Co.`, cp.state].filter(Boolean).join(', ') || '—'}</td>
                      <td className="py-1 pr-4">{formatPhone(cp.phone)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Conditions (always-present statutory conditions, e.g. §309(h)) */}
        {ref.conditions.boilerplate.length > 0 && (
          <Section title="Conditions">
            <div className="space-y-2 text-xs text-gray-800 leading-relaxed">
              {ref.conditions.boilerplate.map((c, i) => (
                <p key={i}>{c}</p>
              ))}
            </div>
          </Section>
        )}

        {/* Waivers/Conditions (verbatim text resolved from SC condition codes) */}
        {(ref.conditions.waivers.length > 0 || ref.conditions.specific_codes.length > 0) && (
          <Section title="Waivers / Conditions">
            <div className="space-y-2 text-xs text-gray-800 leading-relaxed">
              {ref.conditions.waivers.map((w) => (
                <p key={w.code}>{w.text}</p>
              ))}
              {ref.conditions.specific_codes.length > 0 && (
                <p className="text-gray-500">
                  Additional condition code(s) on file: {ref.conditions.specific_codes.join(', ')}
                </p>
              )}
            </div>
          </Section>
        )}

        {/* Associated call signs */}
        {ref.associated_call_signs.length > 0 && (
          <Section title="Associated Call Signs">
            <div className="flex flex-wrap gap-2">
              {ref.associated_call_signs.map((cs) => (
                <Link key={cs} to={`/licenses/${cs}`} className="font-mono text-sm text-primary-600 hover:underline print:text-gray-900 print:no-underline">
                  {cs}
                </Link>
              ))}
            </div>
          </Section>
        )}

        {/* History */}
        {ref.history.length > 0 && (
          <Section title="License History">
            <div className="overflow-x-auto print:overflow-visible">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-1 pr-6 font-medium">Date</th>
                    <th className="py-1 pr-6 font-medium">Action</th>
                    <th className="py-1 pr-6 font-medium">File Number</th>
                  </tr>
                </thead>
                <tbody>
                  {ref.history.map((h) => (
                    <tr key={h.id} className="border-t border-gray-100">
                      <td className="py-1 pr-6">{formatDate(h.log_date)}</td>
                      <td className="py-1 pr-6 font-mono">{h.action_code || '—'}</td>
                      <td className="py-1 pr-6 font-mono text-gray-500">{h.uls_file_number || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </div>
    </div>
  )
}
